import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import React from "react";
import { ResolveThunks } from "react-redux";

type SetFieldValue = (parameter: { key: string; value: string }) => void;

type UseDynamicFormFileUploadParams = {
    fieldKey: string;
    nameKey: string;
    isRequired?: boolean;
    setDynamicFormFieldValue: SetFieldValue;
} & ResolveThunks<{
    setIsBadFile: typeof setIsBadFile;
    setUploadLimitExceeded: typeof setUploadLimitExceeded;
}>;

/**
 * Shared PDF upload state and validation for Dynamic Form upload widgets (max 3MB).
 */
export function useDynamicFormFileUpload({
    fieldKey,
    nameKey,
    setDynamicFormFieldValue,
    setIsBadFile,
    setUploadLimitExceeded,
}: UseDynamicFormFileUploadParams) {
    const fileUploadRef = React.useRef({ value: "" } as HTMLInputElement);
    const [fileField, setFileField] = React.useState<File | null>(null);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [isIncorrectSize, setIsIncorrectSize] = React.useState(false);
    const [isIncorrectExtension, setIsIncorrectExtension] = React.useState(false);

    React.useEffect(() => {
        if (fileField) {
            setIsIncorrectExtension(getFileExtension(fileField.name)?.toLowerCase?.() !== "pdf");
            setIsIncorrectSize(fileField.size > 3e6);
        } else {
            setIsIncorrectExtension(false);
            setIsIncorrectSize(false);
        }
    }, [fileField]);

    /** Validates selected PDF type and size (max 3MB). */
    const validate = React.useCallback(() => {
        let message = "";
        if (fileField && isIncorrectExtension) {
            message = translate("File type must be PDF");
            setIsBadFile({ isBadFile: true });
        } else if (fileField && isIncorrectSize) {
            message = translate("File size exceeds 3MB");
            setUploadLimitExceeded({ uploadLimitExceeded: true });
        }
        setErrorMessage(message);
        return !message;
    }, [fileField, isIncorrectExtension, isIncorrectSize, setIsBadFile, setUploadLimitExceeded]);

    React.useEffect(() => {
        if (fileField) {
            validate();
        }
    }, [fileField, validate]);

    /** Reads a selected file as a data URL into Dynamic Form state. */
    const readFile = (file: File) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            setDynamicFormFieldValue({ key: fieldKey, value: reader.result as string });
            setDynamicFormFieldValue({ key: nameKey, value: file.name });
        };
        reader.readAsDataURL(file);
    };

    /** Handles file input change and clears stored values when the file is removed. */
    const handleFileChange = (event: React.ChangeEvent<any>) => {
        const file = event.target.files?.[0];
        if (file) {
            readFile(file);
            setFileField(file);
        } else {
            setDynamicFormFieldValue({ key: fieldKey, value: "" });
            setDynamicFormFieldValue({ key: nameKey, value: "" });
            setFileField(null);
            setErrorMessage("");
        }
    };

    /** Clears local file state without touching other form fields. */
    const clearLocalFile = React.useCallback(() => {
        setFileField(null);
        setErrorMessage("");
    }, []);

    return {
        fileField,
        errorMessage,
        fileUploadRef,
        handleFileChange,
        clearLocalFile,
        validate,
    };
}
